<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { i18n } from '@/i18n';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import { ReminderMailData } from '../useCases/saveReminderMailSettingsUseCase';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

/**
 * リマインダーメール設定モーダルコンポーネント
 */
/**
 * コンポーネントのprops定義
 */
interface Props {
  /** モーダルの表示状態 */
  modalState?: boolean;
  /** 発注ID */
  orderId?: string;
  /** 既存メッセージ（任意） */
  initialMessage?: string;
  /** 既存リマインド日 YYYYMMDD（任意） */
  initialReminderDate?: string | null;
  /** 既存の受信者（任意） */
  initialRecipients?: string[];
}

// eslint-disable-next-line jsdoc/require-jsdoc
const props = withDefaults(defineProps<Props>(), {
  modalState: false,
  orderId: '',
  initialMessage: '',
  initialReminderDate: null,
  // eslint-disable-next-line jsdoc/require-jsdoc
  initialRecipients: () => [],
});

// emits
const emits = defineEmits<{
  (e: 'closeModal'): void;
  (e: 'saveReminderMail', reminderMailData: ReminderMailData): void;
  (e: 'deleteReminderMail'): void;
}>();

// 変数
const t = i18n.t;
const locale = computed(() => i18n.locale);
const MAX_MESSAGE_LENGTH = 255;

// 最小選択日付（9時以降は翌日0時、それ以前は当日0時）
const minDate = ref<Date>(new Date());

/**
 * 指定した日付の0時を取得する
 * @param d - 日付
 */
const startOfDay = (d: Date): Date => {
  const res = new Date(d);
  res.setHours(0, 0, 0, 0);
  return res;
};

/**
 * 指定した日付の0時を取得する
 */
const startOfTomorrow = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * 最小選択日付を更新する（9時以降は翌日0時、それ以前は当日0時）
 */
const updateMinDate = () => {
  const now = new Date();
  const nineAM = new Date();
  nineAM.setHours(9, 0, 0, 0);
  const newMin = now.getTime() > nineAM.getTime() ? startOfTomorrow() : startOfDay(now);
  minDate.value = newMin;

  // 現在選択中の日付が範囲外になった場合はクリア
  if (selectedDate.value && selectedDate.value < newMin) {
    selectedDate.value = null;
    fieldState['reminderDate'].setValue('');
  }
};

// 状態: 初期リマインド日の有無で表示を切替
const isReminderSet = computed(() => !!props.initialReminderDate);

// 表示用に YYYYMMDD -> YYYY/MM/DD へ整形
const formattedReminderDate = computed(() => {
  const v = props.initialReminderDate;
  if (!v || v.length !== 8) return '';
  const y = v.slice(0, 4);
  const m = v.slice(4, 6);
  const d = v.slice(6, 8);
  return `${y}/${m}/${d}`;
});

// フォームフィールド定義
const fieldItems = computed((): FieldItem[] => {
  return [
    {
      name: 'message',
      rules: [ValidateRule.Required, ValidateRule.MemoInvalidChar, ValidateRule.MemoTooMuchLength],
    },
    {
      name: 'reminderDate',
      rules: [ValidateRule.Required],
    },
    {
      name: 'recipients',
      rules: [ValidateRule.Required],
    },
  ];
});

// useFormを使用してフォーム管理
const { fieldState, allValidate, resetForm, isAllValid } = useForms(fieldItems.value);

// 日付フィールドのための追加の状態
const selectedDate = ref<Date | null>(null);

// 受信者を独立して管理
const recipients = ref<string[]>([]);

// 組織ユーザーリスト
const organizationUsers = ref<string[]>([]);

// 文字数カウント
const messageLength = computed(() => fieldState['message'].value.value.length);
const messageCountText = computed(() => `${messageLength.value}/${MAX_MESSAGE_LENGTH}`);

/**
 * メッセージ入力時の処理
 * @param event - 入力イベント
 */
const handleMessageInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  if (target) {
    fieldState['message'].setValue(target.value);
  }
};

/**
 * リマインダー日付をリセットする
 */
const resetReminderDate = () => {
  selectedDate.value = null;
  fieldState['reminderDate'].setValue('');
};

/**
 * 日付変更時の処理
 * @param date - 選択された日付
 */
const handleDateChange = (date: Date | null) => {
  selectedDate.value = date;
  if (date) {
    // ローカルタイムでYYYY-MM-DDに整形（UTCシフト防止）
    const dateString = DateService.convertDateStrToDateWithHyphen(date);
    fieldState['reminderDate'].setValue(dateString);
  } else {
    fieldState['reminderDate'].setValue('');
  }
};

/**
 * 受信者変更時の処理
 * @param selectedUsers - 選択されたユーザー配列
 */
const handleRecipientsChange = (selectedUsers: string[]) => {
  const recipientsString = selectedUsers.join(',');
  fieldState['recipients'].setValue(recipientsString);
};

/**
 * 受信者の現在選択されている配列を取得
 */
const currentRecipients = computed(() => {
  const value = fieldState['recipients'].value.value;
  return value ? value.split(',').filter((r) => r.trim() !== '') : [];
});

// watchの停止関数を格納する配列
const watchStopHandlers: Array<() => void> = [];

// recipientsの変更を監視してfieldStateを更新（メモリリーク対策）
const stopRecipientsWatch = watch(
  recipients,
  (newRecipients) => {
    const recipientsString = newRecipients.length > 0 ? newRecipients.join(',') : '';
    fieldState['recipients'].setValue(recipientsString);
  },
  { deep: true }
);

// watch停止関数を配列に追加
watchStopHandlers.push(stopRecipientsWatch);

/**
 * YYYYMMDD形式の文字列をDateに変換する
 * @param yyyymmdd - 変換対象の日付文字列（YYYYMMDD）
 * @returns 変換後のDate。無効な場合はnull
 */
const parseYYYYMMDDToDate = (yyyymmdd: string): Date | null => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return null;
  const y = Number(yyyymmdd.slice(0, 4));
  const m = Number(yyyymmdd.slice(4, 6));
  const d = Number(yyyymmdd.slice(6, 8));
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

/**
 * モーダルのフォームに初期値を適用する
 */
const applyInitialValues = () => {
  // メッセージ
  fieldState['message'].setValue(props.initialMessage ?? '');

  // 日付
  const parsed = props.initialReminderDate ? parseYYYYMMDDToDate(props.initialReminderDate) : null;
  selectedDate.value = parsed;
  if (parsed) {
    // ローカルタイムでYYYY-MM-DDに整形（UTCシフト防止）
    const dateString = DateService.convertDateStrToDateWithHyphen(parsed);
    fieldState['reminderDate'].setValue(dateString);
  } else {
    fieldState['reminderDate'].setValue('');
  }

  // 受信者
  recipients.value = Array.isArray(props.initialRecipients) ? [...props.initialRecipients] : [];
};

/**
 * モーダルを閉じる
 */
const closeModal = () => {
  resetFormData();
  emits('closeModal');
};

/**
 * リマインダーメール設定を解除（削除）する
 */
const deleteReminderMail = () => {
  emits('deleteReminderMail');
  closeModal();
};

/**
 * リマインダーメール設定を保存する
 */
const saveReminderMail = () => {
  if (allValidate()) {
    const reminderMailData: ReminderMailData = {
      orderId: props.orderId || '',
      message: fieldState['message'].value.value,
      reminderDate: fieldState['reminderDate'].value.value,
      recipients: recipients.value,
    };
    emits('saveReminderMail', reminderMailData);
    closeModal();
  }
};

/**
 * フォームをリセットする
 */
const resetFormData = () => {
  resetForm(fieldItems.value);
  selectedDate.value = null;
  recipients.value = [];
};

// モーダルが開いたら初期値を反映
watch(
  () => props.modalState,
  (isOpen) => {
    if (isOpen) {
      resetFormData();
      updateMinDate();
      applyInitialValues();
    }
  }
);

// 初期マウント時にも最小日付を設定
onMounted(() => {
  updateMinDate();
});

/**
 * コンポーネント破棄時のクリーンアップ処理
 */
onBeforeUnmount(() => {
  // 全てのwatchを停止してメモリリークを防ぐ
  watchStopHandlers.forEach((stopHandler) => {
    stopHandler();
  });
  watchStopHandlers.length = 0;
});
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :enable-outside-click="true"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('設定する')"
    :is-disabled-next-button="!isAllValid"
    @on-next="saveReminderMail"
    @on-back="closeModal"
  >
    <template #title>{{ t('リマインドメール設定') }}</template>
    <template #description>
      <div class="reminder-mail-modal__description">
        {{ t('メモ内容を編集し、リマインド日を設定してください。') }}
      </div></template
    >
    <!-- 設定状態の表示行（Description直下） -->
    <div class="reminder-mail-status">
      <template v-if="!isReminderSet">
        <Mdi class="reminder-mail-status__icon" icon="calendar_today" />
        <span class="reminder-mail-status__label">{{ t('未設定') }}</span>
      </template>
      <template v-else>
        <span class="reminder-mail-status__label reminder-mail-status__label--set">{{ t('設定済み') }}</span>
        <span class="reminder-mail-status__date">({{ formattedReminderDate }})</span>
        <span class="reminder-mail-status__unlink" role="button" tabindex="0" @click="deleteReminderMail">{{
          t('リマインドメール解除')
        }}</span>
      </template>
    </div>
    <div class="reminder-mail-form">
      <div class="reminder-mail-form__field">
        <div class="reminder-mail-form__memo-header">
          <label class="reminder-mail-form__label">{{ t('メモ内容') }}</label>
          <!-- NOTE: リマインドメールAPIから最終更新日が提供されるまでコメントアウト -->
          <!-- <div class="reminder-mail-form__last-updated">
            {{ t('最終更新日：2023/02/12') }}
          </div> -->
        </div>
        <textarea
          v-model="fieldState['message'].value.value"
          :placeholder="t('255文字以内で記入してください')"
          class="reminder-mail-form__textarea"
          rows="6"
          :maxlength="MAX_MESSAGE_LENGTH"
          @input="handleMessageInput"
        />
        <div class="reminder-mail-form__footer">
          <ErrorMessage
            :is-error="fieldState['message'].isError.value"
            :error-message="fieldState['message'].errorMessage.value"
            class="reminder-mail-form__error"
          />
          <div class="reminder-mail-form__character-count">
            {{ messageCountText }}
          </div>
        </div>
      </div>

      <div class="reminder-mail-form__field">
        <div class="reminder-mail-form__date-header">
          <label class="reminder-mail-form__label">{{ t('リマインド日') }}</label>
          <button type="button" class="reminder-mail-form__reset-button" @click="resetReminderDate">
            {{ t('リセット') }}
          </button>
        </div>
        <div class="reminder-mail-form__date-row">
          <AppDatePicker
            v-model="selectedDate"
            class="reminder-mail-form__date-picker"
            :format="'yyyy-MM-dd'"
            :locale="locale"
            :placeholder="t('未設定')"
            :clearable="false"
            :enable-time-picker="false"
            :min-date="minDate"
            :teleport="true"
            :placement="'top'"
            auto-apply
            @update:model-value="handleDateChange"
          />
        </div>
        <ErrorMessage
          :is-error="fieldState['reminderDate'].isError.value"
          :error-message="fieldState['reminderDate'].errorMessage.value"
          class="reminder-mail-form__error"
        />
      </div>

      <div class="reminder-mail-form__field">
        <label class="reminder-mail-form__label">{{ t('リマインド受信者') }}</label>
        <MultipleSelectInput
          :selected-list="recipients"
          :index="0"
          :filter-type="'STAFF_NAME'"
          :prop-options="organizationUsers"
          :placeholder="t('選択してください')"
          :label="''"
          class="reminder-mail-form__multi-select"
          @update:model-value="(e) => (recipients = e)"
        />
        <ErrorMessage
          :is-error="fieldState['recipients'].isError.value"
          :error-message="fieldState['recipients'].errorMessage.value"
          class="reminder-mail-form__error"
        />
      </div>
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.reminder-mail-modal {
  &__description {
    margin-bottom: 24px;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
  }
}

/* Description直下の設定状態表示 */
.reminder-mail-status {
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  background-color: $semanticWhite;
  margin-bottom: 8px;

  &__icon {
    font-size: 24px;
    opacity: 0.7;
  }

  &__icon-box {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    background: $semanticWhite;
  }

  &__label {
    /* Typography: 未設定 */
    font-family: 'Noto Sans JP', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    color: $textSecondary;

    &--set {
      /* Typography: 設定済み */
      font-family: 'Noto Sans JP', sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 100%;
      letter-spacing: 0;
      vertical-align: middle;
      color: $semanticWarning;
    }
  }

  &__date {
    /* Typography: 日付テキスト */
    font-family: 'Noto Sans JP', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    color: $textDefault;
  }

  &__unlink {
    /* Typography: リマインドメール解除 */
    font-family: 'Noto Sans JP', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    color: $semanticError;
    text-decoration: underline;
    text-decoration-style: solid;
    text-underline-offset: 0;
    text-decoration-thickness: auto;
  }
}

.reminder-mail-form {
  display: grid;
  gap: 24px;
  padding: 0;

  &__memo-header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
  }

  &__field {
    display: grid;
    gap: 8px;
  }

  &__label {
    font-family: Noto Sans JP;
    font-weight: 400;
    font-style: Regular;
    font-size: 16px;
    line-height: 100%;
    letter-spacing: 0%;
    vertical-align: middle;

    color: $textDefault;
    text-align: left;
  }

  &__last-updated {
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $textSecondary;
    text-align: right;
  }

  &__textarea {
    width: 100%;
    min-height: 120px;
    padding: 12px 16px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: $textDefault;
    background-color: white;
    resize: vertical;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: $semanticPrimary;
      box-shadow: 0 0 0 2px rgba($semanticPrimary, 0.1);
    }

    &::placeholder {
      color: $textSecondary;
    }
  }

  &__character-count {
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $textSecondary;
    text-align: right;
  }

  &__footer {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
  }

  &__date-header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
  }

  &__date-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  &__date-picker {
    width: 100%;
    min-width: 0;
  }

  &__reset-button {
    padding: 8px 16px;
    border: 1px solid $semanticError;
    border-radius: 4px;
    background-color: transparent;
    color: $semanticError;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background-color: rgba($semanticError, 0.04);
    }

    &:active {
      background-color: rgba($semanticError, 0.08);
    }
  }

  &__multi-select {
    width: 100%;
  }

  &__error {
  }
}
</style>

<style lang="scss">
/* VueDatePickerのカスタムスタイル */
.reminder-mail-form__date-picker {
  .dp__input {
    width: 100%;
    height: 40px;
    padding: 8px 16px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: $textDefault;
    background-color: white;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: $semanticPrimary;
      box-shadow: 0 0 0 2px rgba($semanticPrimary, 0.1);
    }

    &::placeholder {
      color: $textSecondary;
    }
  }

  .dp__input_icon {
    color: $textSecondary;
  }
}
</style>
